import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useBbox, useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import Box from 'components/atoms/Layout/Box/Box';
import ClientProfile from 'components/organisms/Profiles/ClientProfile/ClientProfile';
import SettingsHeaderWrapper from 'components/templates/Wrappers/Settings/SettingsHeaderWrapper/SettingsHeaderWrapper';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import StyledPlanSettingPage from 'components/pages/Setting/PlanSettingPage/PlanSettingPage.styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Save from '@mui/icons-material/Save';
import utils from 'helpers/utils';
import Card from 'components/atoms/Cards/Card/Card';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import Typography, {H5} from 'components/atoms/Text/Typography/Typography';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import Icon from 'components/atoms/Icons/Icon/Icon';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';

const info = `Select the plan that suits your needs. Custom plans can only be changed by your ${constants.basics.companyName} representative`;

const PlanSettingPage = (props) => {
  const innerProps = useComponentProps(props, 'PlanSettingPage');

  const formRef = useRef(null);
  const contentRef = useRef(null);

  const [internalState, setInternalState] = useState({
    price: 0,
    submitting: false
  });

  const profileProvider = useProfile();
  const isLoading = profileProvider.isLoading();
  const client = profileProvider.data?.data;

  const updateEvent = useEffectEvent(profileProvider.updaters?.updateData);

  const authorize = useAuthorize();
  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const bBox = useBbox(() => contentRef.current, ['width']);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const lsDown = useMediaQuery((theme) => theme.breakpoints.down('ls'));

  useLayoutEffect(() => {
    const plan = constants.data.lookup('plans', client?.props?.plan);

    if (plan) {
      setInternalState(utils.updater({
        price: Math.max(0, Math.ceil((client?.props?.monthlyCreditLimit - plan.credits) / plan.bucketSize) * plan.bucketPrice)
      }, true));
    }
  }, [client?.props]);

  const fieldsMemo = useMemo(() => {
    const plan = constants.data.lookup('plans', client?.props?.plan);

    if (plan && plan.value !== constants.client.plan.types.custom) {
      const options = (new Array(16 + 1)).fill(null).map((v, idx) => ({
        label: `${utils.numberToLabel(plan.bucketSize * idx)}`,
        value: idx
      }));

      return [
        {
          name: 'extraCredits',
          label: 'Extra credits',
          type: constants.formFieldTypes.slider,
          options: options,
          entity: 'client',
          initial: Math.max(0, Math.ceil((client?.props?.monthlyCreditLimit - plan.credits) / plan.bucketSize)),
          disabled: !authorize({attribute: 'settings.plan.update'}),
          FormFieldProps: {
            variant: 'staticLabel',
            showLabels: !lsDown
          },
        }
      ]
    }
  }, [client?.props, authorize, lsDown]);

  const saveAction = useMemo(() => ({
    label: 'Save changes',
    auth: utils.createAuth({attribute: 'settings.plan.update'}),
    icon: Save,
    onClick: (e) => {
      formRef.current?.submit();
      e.preventDefault();
    },
    ButtonProps: {
      disabled: internalState.submitting
    }
  }), [internalState.submitting]);

  const handleShowCard = (card) => {
    return card.name.startsWith('plan');
  };

  const handleCanUpdate = () => {
    return authorize({attribute: 'settings.plan.update'});
  }

  const handleDirty = (dirty) => {
    profileProvider.dirty?.(dirty);
  }

  const handleChangeDirect = useCallback((e) => {
    const field = fieldsMemo?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    if (field.name === 'extraCredits') {
      const plan = constants.data.lookup('plans', client?.props?.plan);

      if (plan) {
        setInternalState(utils.updater({
          price: Math.max(0, value * plan.bucketPrice)
        }, true));
      }
    }
  }, [client?.props?.plan, fieldsMemo]);

  const handleSubmit = useCallback((values, actions) => {
    setInternalState(utils.updater({submitting: true}, true));

    const plan = constants.data.lookup('plans', client?.props?.plan);
    const changes = {
      monthlyCreditLimit: plan.credits + values.extraCredits * plan.bucketSize
    };

    let nextRenew = (new Date());
    nextRenew.setMonth(nextRenew.getMonth() + 1);
    nextRenew = new Date(`${nextRenew.toISOString().split('-').slice(0, 2).join('-')}-01T00:00:00.000Z`);

    const daysToNext = Math.floor((nextRenew.getTime() - (new Date()).getTime()) / (24 * 60 * 60 * 1000));
    const upgradeWarning = !(+changes.monthlyCreditLimit > +client?.props?.monthlyCreditLimit) ? null : ({
      question: 'Are you sure you want to add extra credits?',
      explanation: `Adding extra credits will take effect immediately${daysToNext <= 7 ? `. Be aware there are ${daysToNext} days left this month to use the extra credits` : ''}`
    });
    const downgradeWarning = !(+changes.monthlyCreditLimit < +client?.props?.monthlyCreditLimit) ? null : ({
      question: 'Are you sure you want to remove your extra credits?',
      explanation: 'Removing extra credits will take effect on the first day off the next month'
    });

    const doSubmit = (confirm = false) => {
      return updateEvent?.(client, {}, changes)
        .then(() => {
          snackbar.show('Plan was saved', null,
            {color: 'success', autoHideDuration: constants.delay.success});
        })
        .catch(() => {
          if (confirm) {
            throw new Error('Saving plan failed');
          } else {
            snackbar.show('Saving plan failed', null,
              {color: 'error', autoHideDuration: constants.delay.error});
          }
        })
        .finally(() => {
          setInternalState(utils.updater({submitting: false}, true));
          actions.setSubmitting(false);
        });
    }

    if (upgradeWarning || downgradeWarning) {
      const handleConfirm = () => {
        return doSubmit(true);
      }

      const handleClose = () => {
        setInternalState(utils.updater({submitting: false}, true));
        actions.setSubmitting(false);
        dialogControl.hide();
      }

      dialogControl.show(<ConfirmDialog question={upgradeWarning ? upgradeWarning.question : downgradeWarning.question}
                                        explanation={upgradeWarning ? upgradeWarning.explanation : downgradeWarning.explanation}
                                        onConfirm={handleConfirm}
                                        ConfirmButtonProps={{
                                          startIcon: <Icon icon={Save}/>,
                                          children: 'Save',
                                          color: 'primary'
                                        }}/>, true, handleClose);
    } else {
      doSubmit(false);
    }
  }, [client, dialogControl, snackbar, updateEvent]);

  const dirtyEvent = useEffectEvent(profileProvider.dirty);
  const handleValidating = useCallback((isValidating, isDirty) => {
    dirtyEvent?.(isDirty);
  }, [dirtyEvent]);

  const columns = bBox?.width ? Math.max(Math.floor((bBox?.width ?? 0) / 286), 1) : 0;

  return <StyledPlanSettingPage as={SettingsHeaderWrapper} {...innerProps}
                                title="Plan"
                                info={!smDown ? info : null}>
    <Box ref={contentRef} className="PlanSettingPage-content">
      <ClientProfile columns={columns}
                     variant="grid"
                     onShowCard={handleShowCard}
                     onDirty={handleDirty}
                     onCanUpdate={handleCanUpdate}/>

      {fieldsMemo?.length > 0 ? <Card className="PlanSettingPage-options"
                                      elevation={1}
                                      radius="round">
        <Box className="PlanSettingPage-options-price">
          <H5 isLoading={isLoading}>+ &euro; {utils.formatNumber(internalState?.price)}</H5>
          <Typography variant="subtitle2" isLoading={isLoading}>per month</Typography>
        </Box>
        <InlineForm ref={formRef}
                    autoFocus={false}
                    className="PlanSettingPage-options-credits"
                    onChangeDirect={handleChangeDirect}
                    onSubmit={handleSubmit}
                    onValidating={handleValidating}
                    fields={fieldsMemo} />
        <Box className="PlanSettingPage-options-save">
          <ActionButton action={saveAction}/>
        </Box>
      </Card> : null}
    </Box>
  </StyledPlanSettingPage>
};

PlanSettingPage.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default PlanSettingPage;
